import * as React from 'react'
import { ComponentProps, ForwardRefExoticComponent, RefAttributes, SVGProps } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { AttendanceStateResolverStateEnum } from 'generated/resources/graphql'

import { buildUrl } from 'modules/core/lib/buildUrl'
import { minutesToText } from 'modules/core/lib/duration'
import i18n from 'modules/core/lib/i18n'

import { getClockInStatusBadge } from 'modules/attendance/components/TimeTrackingCollection/components/ClockInStatusBadge'
import { aggregateMinutes } from 'modules/attendance/components/TimeTrackingCollection/lib/aggregateMinutes'
import { buildHourDistributionDataPoints } from 'modules/attendance/components/TimeTrackingCollection/lib/hourDistribution'
import { TimeTrackingCollectionEmployee } from 'modules/attendance/components/TimeTrackingCollection/types'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'

import { useAlerts } from './useAlerts'

/**
 * Extracts the attendance policy settings for an employee
 */
const getEmployeePolicySettings = (employee: TimeTrackingCollectionEmployee) => {
  return employee.attendanceEmployeesPolicy?.attendanceEmployeesSettingsConnection?.edges?.[0]?.node
}

/**
 * Extracts the extra hours baseline from employee policy settings
 */
const getExtraHoursBaseline = (employee: TimeTrackingCollectionEmployee) => {
  return getEmployeePolicySettings(employee)?.extraHoursBaseline
}

/**
 * Extracts the complementary hours baseline from employee policy settings
 */
const getComplementaryHoursBaseline = (employee: TimeTrackingCollectionEmployee) => {
  return getEmployeePolicySettings(employee)?.complementaryHoursBaseline
}

type OneDataCollectionProps = ComponentProps<typeof OneDataCollection>
type Visualizations = OneDataCollectionProps['visualizations']
type TableVisualization = Extract<Visualizations[number], { type: 'table' }>
type ColumnsType = TableVisualization['options']['columns'][number]

type ColumnType = Omit<ColumnsType, 'render' | 'sorting'> & {
  render: (item: TimeTrackingCollectionEmployee) =>
    | string
    | { type: 'text'; value: string }
    | {
        type: 'person'
        value: {
          firstName: string
          lastName: string
          avatar: string | null | undefined
          badge?:
            | {
                type: 'critical' | 'positive' | 'warning'
                icon: ForwardRefExoticComponent<
                  Omit<SVGProps<SVGSVGElement>, 'ref'> & RefAttributes<SVGSVGElement>
                >
              }
            | undefined
        }
      }
    | {
        type: 'status'
        value: { status: string; label: string }
      }
    | {
        type: 'icon'
        value: {
          icon: ForwardRefExoticComponent<
            Omit<SVGProps<SVGSVGElement>, 'ref'> & RefAttributes<SVGSVGElement>
          >
          info: string
        }
      }
    | ReturnType<ColumnsType['render']>
    | undefined
} & {
  sorting?: 'firstName' | 'lastName' | 'alertsCount' | 'balance' | undefined
}

const reviewStatusToStatus = (
  reviewStatus: AttendanceStateResolverStateEnum | null | undefined
) => {
  if (!reviewStatus) {
    return {
      status: 'neutral',
      label: i18n.t('time_tracking.table_collection.review_status_in_progress'),
    }
  }

  switch (reviewStatus) {
    case 'approved':
      return {
        status: 'positive',
        label: i18n.t('time_tracking.table_collection.review_status_approved'),
      }
    case 'pending':
      return {
        status: 'warning',
        label: i18n.t('time_tracking.table_collection.review_status_pending'),
      }
    default:
      return {
        status: 'neutral',
        label: i18n.t('time_tracking.table_collection.review_status_in_progress'),
      }
  }
}

export const useColumns = ({
  balanceSortingEnabled = true,
}: { balanceSortingEnabled?: boolean } = {}) => {
  const { dateFrom, dateTo } = useAttendanceDateNavigator()
  const { alertsColumn } = useAlerts()
  const [showComplementaryHours, setShowComplementaryHours] = React.useState(false)
  const { featureAvailability: complementaryHoursEnabled } = useAttendanceFeatures(
    'dev_time_tracking_complementary_hours'
  )

  const showComplementaryHoursAndEnabled = complementaryHoursEnabled && showComplementaryHours
  const { featureAvailability: hasHourBalance } = useAttendanceFeatures('dev_hour_balance_ui')
  const { featureAvailability: canSeeHourBalance } =
    useAttendanceFeatures('hour_balance_visibility')
  const showHourBalance = hasHourBalance && canSeeHourBalance

  const columns: ColumnType[] = [
    {
      label: i18n.t('time_tracking.table_collection.employee'),
      sorting: 'firstName',
      render: (item: TimeTrackingCollectionEmployee) => {
        const badge = getClockInStatusBadge(item.clockInStatus)

        return {
          type: 'person' as const,
          value: {
            firstName: item.firstName,
            lastName: item.lastName,
            avatar: item.access.avatar,
            src: buildUrl(item.access.avatar ?? ''),
            badge: badge || undefined,
          },
        }
      },
    },
    ...alertsColumn,
    {
      label: i18n.t('time_tracking.table.headers.distribution'),
      render: (item: TimeTrackingCollectionEmployee) => {
        if (item.isDaily) {
          return { type: 'text' as const, value: '-' }
        }
        const workedTimeBlocks =
          item.attendanceAggregatedWorkedTime?.workedTimeBlocks ?? undefined
        const estimatedTimes = item.attendanceEstimatedTimes ?? undefined
        const estimatedByDate =
          estimatedTimes?.map((e) => ({ date: e.date, minutes: e.minutes })) ?? undefined
        const dataPoints = buildHourDistributionDataPoints(
          dateFrom,
          dateTo,
          workedTimeBlocks,
          estimatedByDate
        )
        // eslint-disable-next-line no-type-assertion/no-type-assertion
        return { type: 'hourDistribution', value: { dataPoints } } as unknown as ReturnType<
          ColumnsType['render']
        >
      },
    },
    ...(showHourBalance
      ? [
          {
            label: i18n.t('time_tracking.table_collection.worked_with_planned_hours'),
            info: i18n.t(
              'time_tracking.table_collection.effective_worked_with_planned_hours_tooltip'
            ),
            render: (item: TimeTrackingCollectionEmployee) => {
              const { workedMinutes, plannedMinutes, dailyDays } = aggregateMinutes({
                employee: item,
                extraHoursBaselineOverride: getExtraHoursBaseline(item),
                complementaryHoursFeatureEnabled: complementaryHoursEnabled,
              })

              const isDaily = item.isDaily
              if (isDaily) {
                return {
                  type: 'text' as const,
                  value: i18n.t('time_tracking.day_abbreviation', {
                    numberOfDays: dailyDays,
                  }),
                }
              }

              return {
                type: 'text' as const,
                value: `${minutesToText(workedMinutes, true)} / ${minutesToText(
                  plannedMinutes,
                  true
                )}`,
              }
            },
          },
        ]
      : []),
    ...(showHourBalance
      ? [
          {
            label: i18n.t('time_tracking.table_collection.balance'),
            info: i18n.t('time_tracking.table_collection.balance_tooltip'),
            ...(balanceSortingEnabled ? { sorting: 'balance' as const } : {}),
            render: (item: TimeTrackingCollectionEmployee) => {
              const isDaily = item.isDaily
              if (isDaily) {
                return {
                  type: 'text' as const,
                  value: '-',
                }
              }

              const { balanceMinutes } = aggregateMinutes({
                employee: item,
                extraHoursBaselineOverride: getExtraHoursBaseline(item),
                complementaryHoursFeatureEnabled: complementaryHoursEnabled,
              })

              const zeroAlignment = balanceMinutes === 0 ? '\u2007' : ''

              return {
                type: 'text' as const,
                value: `${zeroAlignment}${minutesToText(balanceMinutes, true, true)}`,
              }
            },
          },
          {
            label: i18n.t('time_tracking.table_collection.extra_hours'),
            info: i18n.t('time_tracking.table_collection.extra_hours_tooltip'),
            render: (item: TimeTrackingCollectionEmployee) => {
              const isDaily = item.isDaily
              if (isDaily) {
                return {
                  type: 'text' as const,
                  value: '-',
                }
              }

              const { extraTime } = aggregateMinutes({
                employee: item,
                extraHoursBaselineOverride: getExtraHoursBaseline(item),
                complementaryHoursFeatureEnabled: complementaryHoursEnabled,
              })
              const extraTimeMinutes = extraTime.minutes

              return extraTimeMinutes === undefined
                ? '-'
                : {
                    type: 'text' as const,
                    value: minutesToText(extraTimeMinutes, true),
                  }
            },
          },
        ]
      : []),
    ...(showHourBalance && showComplementaryHoursAndEnabled
      ? [
          {
            label: i18n.t('time_tracking.table_collection.complementary_hours'),
            info: i18n.t('time_tracking.table_collection.complementary_hours_tooltip'),
            render: (item: TimeTrackingCollectionEmployee) => {
              const isDaily = item.isDaily
              if (isDaily) {
                return {
                  type: 'text' as const,
                  value: '-',
                }
              }

              const { complementaryTime } = aggregateMinutes({
                employee: item,
                complementaryHoursBaselineOverride: getComplementaryHoursBaseline(item),
                complementaryHoursFeatureEnabled: complementaryHoursEnabled,
              })
              const complementaryTimeMinutes = complementaryTime.minutes

              return complementaryTimeMinutes === undefined
                ? '-'
                : {
                    type: 'text' as const,
                    value: minutesToText(complementaryTimeMinutes, true),
                  }
            },
          },
        ]
      : []),
    {
      label: i18n.t('time_tracking.table_collection.review_status'),
      render: (item: TimeTrackingCollectionEmployee) => {
        return {
          type: 'status' as const,
          value: reviewStatusToStatus(item.reviewStatus),
        }
      },
    },
  ]

  return {
    columns,
    setShowComplementaryHours,
  }
}
